/**
 * A1 — Ficha del cliente: el contenedor de todo lo suyo (RF-TOGA-001).
 * Expedientes, documentos, presupuestos, facturación y comunicación cuelgan de aquí;
 * las pantallas globales del bufete siguen existiendo como vistas transversales.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarClock,
  Coins,
  FileText,
  LayoutGrid,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Receipt,
  RotateCw,
  Scale,
  User as UserIcon,
  Users,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import { useAuth } from '../contexts/AuthContext'
import ClientFormModal from '../components/clients/ClientFormModal'
import ContactsPanel from '../components/clients/ContactsPanel'
import ClientBillingTab from '../components/clients/ClientBillingTab'
import {
  ClientCasesTab,
  ClientDocumentsTab,
  ClientQuotesTab,
} from '../components/clients/ClientTabs'
import ClientCommunicationsTab from '../components/clients/ClientCommunicationsTab'
import type { ClientDetail, ClientSummary } from '@toga/shared'

type TabId =
  | 'resumen'
  | 'contactos'
  | 'expedientes'
  | 'documentos'
  | 'presupuestos'
  | 'facturacion'
  | 'comunicacion'

const TABS: Array<{
  id: TabId
  label: string
  icon: typeof Scale
  count?: (s: ClientSummary) => number
}> = [
  { id: 'resumen', label: 'Resumen', icon: LayoutGrid },
  { id: 'contactos', label: 'Contactos', icon: Users },
  { id: 'expedientes', label: 'Expedientes', icon: Scale, count: (s) => s.cases.total },
  { id: 'documentos', label: 'Documentos', icon: FileText, count: (s) => s.documents.total },
  { id: 'presupuestos', label: 'Presupuestos', icon: FileText, count: (s) => s.quotes.total },
  { id: 'facturacion', label: 'Facturación', icon: Receipt, count: (s) => s.billing.invoicesCount },
  { id: 'comunicacion', label: 'Comunicación', icon: Mail, count: (s) => s.communications.total },
]

const isTab = (v: string | null): v is TabId => TABS.some((t) => t.id === v)

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const canWrite = role !== null && role !== 'LECTURA'
  const canCreateCase = role === 'ADMIN' || role === 'ABOGADO'

  const [searchParams, setSearchParams] = useSearchParams()
  const tab: TabId = isTab(searchParams.get('tab')) ? (searchParams.get('tab') as TabId) : 'resumen'
  const goTab = (t: TabId) => {
    const next = new URLSearchParams(searchParams)
    if (t === 'resumen') next.delete('tab')
    else next.set('tab', t)
    setSearchParams(next, { replace: true })
  }

  const [client, setClient] = useState<ClientDetail | null>(null)
  const [summary, setSummary] = useState<ClientSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [editing, setEditing] = useState(false)

  const loadSummary = useCallback(async () => {
    if (!id) return
    try {
      const r = await api.get<{ summary: ClientSummary }>(`/clients/${id}/summary`)
      setSummary(r.summary)
    } catch {
      // Los contadores son un adorno: si fallan, la ficha sigue siendo usable.
    }
  }, [id])

  const loadClient = useCallback(async () => {
    if (!id) return
    setError(null)
    try {
      const r = await api.get<{ client: ClientDetail }>(`/clients/${id}`)
      setClient(r.client)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotFound(true)
      else setError(err instanceof Error ? err.message : 'No se pudo cargar el cliente')
    }
  }, [id])

  useEffect(() => {
    setClient(null)
    setSummary(null)
    setNotFound(false)
    void loadClient()
    void loadSummary()
  }, [loadClient, loadSummary])

  if (notFound || error) {
    return (
      <div className="surface p-8 text-center max-w-lg mx-auto animate-slideUp">
        <AlertCircle size={28} className="mx-auto text-rose-300 mb-2" />
        <p className="t-primary font-medium mb-1">
          {notFound ? 'Cliente no encontrado' : 'No se pudo cargar el cliente'}
        </p>
        {error && <p className="t-muted text-sm mb-3">{error}</p>}
        <Link to="/clients" className="text-sm text-toga-300 hover:underline">
          ← Volver a clientes
        </Link>
      </div>
    )
  }
  if (!client) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando cliente…
      </div>
    )
  }

  const missingFiscal = !client.nationalId || !client.address
  const refreshAll = () => {
    void loadSummary()
  }

  return (
    <div className="space-y-5 animate-slideUp">
      <header className="space-y-3">
        <Link to="/clients" className="text-xs t-faint hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Clientes
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${
                client.type === 'EMPRESA'
                  ? 'bg-indigo-500/15 text-indigo-300'
                  : 'bg-purple-500/15 text-purple-300'
              }`}
            >
              {client.type === 'EMPRESA' ? <Building2 size={20} /> : <UserIcon size={20} />}
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-serif font-semibold t-primary truncate">
                {client.displayName}
              </h1>
              <div className="text-xs t-muted flex items-center gap-x-3 gap-y-1 flex-wrap mt-0.5">
                <span>{client.type === 'EMPRESA' ? 'Persona jurídica' : 'Persona física'}</span>
                {client.nationalId && <span className="font-mono">{client.nationalId}</span>}
                {client.email && (
                  <a href={`mailto:${client.email}`} className="hover:underline inline-flex items-center gap-1">
                    <Mail size={11} /> {client.email}
                  </a>
                )}
                {(client.mobile ?? client.phone) && (
                  <a
                    href={`tel:${client.mobile ?? client.phone}`}
                    className="hover:underline inline-flex items-center gap-1"
                  >
                    <Phone size={11} /> {client.mobile ?? client.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <Pencil size={14} /> Editar datos
            </button>
          )}
        </div>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-base" aria-label="Secciones del cliente">
        {TABS.map((t) => {
          const active = tab === t.id
          const n = summary && t.count ? t.count(summary) : null
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => goTab(t.id)}
              aria-current={active ? 'page' : undefined}
              className={`px-3.5 py-2.5 text-sm whitespace-nowrap flex items-center gap-2 border-b-2 -mb-px transition ${
                active
                  ? 'border-toga-400 text-toga-200'
                  : 'border-transparent t-muted hover:t-secondary'
              }`}
            >
              <t.icon size={14} />
              {t.label}
              {n !== null && n > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700/50 t-secondary">
                  {n}
                </span>
              )}
            </button>
          )
        })}
      </nav>

      {tab === 'resumen' && (
        <ResumenTab client={client} summary={summary} missingFiscal={missingFiscal} goTab={goTab} />
      )}
      {tab === 'contactos' && <ContactsPanel clientId={client.id} canWrite={canWrite} />}
      {tab === 'expedientes' && (
        <ClientCasesTab clientId={client.id} canCreate={canCreateCase} onChanged={refreshAll} />
      )}
      {tab === 'documentos' && (
        <ClientDocumentsTab clientId={client.id} canWrite={canWrite} onChanged={refreshAll} />
      )}
      {tab === 'presupuestos' && <ClientQuotesTab clientId={client.id} canWrite={canWrite} />}
      {tab === 'facturacion' && (
        <ClientBillingTab clientId={client.id} canWrite={canWrite} summary={summary} />
      )}
      {tab === 'comunicacion' && (
        <ClientCommunicationsTab
          clientId={client.id}
          canWrite={canWrite}
          clientPhone={client.mobile || client.phone}
          onChanged={() => void loadSummary()}
        />
      )}

      {editing && (
        <ClientFormModal
          initial={client}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            void loadClient()
            void loadSummary()
          }}
        />
      )}
    </div>
  )
}

// ---------- Resumen ----------

function Stat({
  icon,
  label,
  value,
  hint,
  tone,
  onClick,
}: {
  icon: ReactNode
  label: string
  value: string
  hint?: string
  tone?: 'warn'
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="surface p-4 text-left hover:border-toga-500/40 transition"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider t-faint">
        {icon}
        {label}
      </div>
      <div
        className={`text-2xl font-semibold font-mono mt-1.5 ${tone === 'warn' ? 'text-amber-300' : 't-primary'}`}
      >
        {value}
      </div>
      {hint && <div className="text-[11px] t-faint mt-0.5">{hint}</div>}
    </button>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5 text-sm">
      <dt className="w-28 shrink-0 t-faint text-xs pt-0.5">{label}</dt>
      <dd className="t-secondary min-w-0">{children}</dd>
    </div>
  )
}

function ResumenTab({
  client,
  summary,
  missingFiscal,
  goTab,
}: {
  client: ClientDetail
  summary: ClientSummary | null
  missingFiscal: boolean
  goTab: (t: TabId) => void
}) {
  const address = [client.address, [client.postalCode, client.city].filter(Boolean).join(' '), client.province]
    .filter(Boolean)
    .join(', ')

  return (
    <div className="space-y-5">
      {missingFiscal && (
        <div className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 flex items-start gap-2.5">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            Faltan {[!client.nationalId && 'el NIF', !client.address && 'el domicilio'].filter(Boolean).join(' y ')}{' '}
            de este cliente. Una factura debe llevar el NIF y el domicilio del destinatario (art. 6
            del RD 1619/2012): complétalos con «Editar datos» antes de facturarle.
          </span>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat
            icon={<Scale size={12} />}
            label="Expedientes activos"
            value={String(summary.cases.active)}
            hint={`${summary.cases.total} en total`}
            onClick={() => goTab('expedientes')}
          />
          <Stat
            icon={<FileText size={12} />}
            label="Documentos"
            value={String(summary.documents.total)}
            onClick={() => goTab('documentos')}
          />
          <Stat
            icon={<FileText size={12} />}
            label="Presupuestos abiertos"
            value={String(summary.quotes.open)}
            hint={summary.quotes.accepted > 0 ? `${summary.quotes.accepted} aceptado(s)` : undefined}
            onClick={() => goTab('presupuestos')}
          />
          <Stat
            icon={<Receipt size={12} />}
            label="Pendiente de cobro"
            value={formatCents(summary.billing.pendingCents)}
            hint={`Facturado ${formatCents(summary.billing.billedCents)}`}
            tone={summary.billing.pendingCents > 0 ? 'warn' : undefined}
            onClick={() => goTab('facturacion')}
          />
          <Stat
            icon={<Coins size={12} />}
            label="Provisión disponible"
            value={formatCents(summary.funds.availableCents)}
            onClick={() => goTab('facturacion')}
          />
          <Stat
            icon={<CalendarClock size={12} />}
            label="Hitos de pago pendientes"
            value={String(summary.milestones.pendingCount)}
            hint={
              summary.milestones.pendingCount > 0
                ? `${formatCents(summary.milestones.pendingCents)} (base)`
                : undefined
            }
            onClick={() => goTab('facturacion')}
          />
          <Stat
            icon={<RotateCw size={12} />}
            label="Igualas activas"
            value={String(summary.recurring.active)}
            onClick={() => goTab('facturacion')}
          />
          <Stat
            icon={<Mail size={12} />}
            label="Comunicaciones"
            value={String(summary.communications.total)}
            onClick={() => goTab('comunicacion')}
          />
        </div>
      )}

      <section className="surface p-5">
        <h3 className="t-primary font-semibold text-sm mb-2">Datos del cliente</h3>
        <dl className="grid md:grid-cols-2 gap-x-8">
          <div>
            <Row label="NIF / CIF">{client.nationalId ?? <span className="t-faint">—</span>}</Row>
            <Row label="Correo">{client.email ?? <span className="t-faint">—</span>}</Row>
            <Row label="Teléfono">{client.phone ?? <span className="t-faint">—</span>}</Row>
            <Row label="Móvil">{client.mobile ?? <span className="t-faint">—</span>}</Row>
          </div>
          <div>
            <Row label="Domicilio">{address || <span className="t-faint">—</span>}</Row>
            <Row label="País">{client.country}</Row>
            <Row label="Alta">
              {new Date(client.createdAt).toLocaleDateString('es-ES')}
              {client.createdByName ? ` · ${client.createdByName}` : ''}
            </Row>
          </div>
        </dl>
        {client.notes && (
          <div className="mt-3 pt-3 border-t border-base/50">
            <div className="t-faint text-xs mb-1">Notas</div>
            <p className="text-sm t-secondary whitespace-pre-wrap">{client.notes}</p>
          </div>
        )}
      </section>
    </div>
  )
}
